/**
 * Local generic Door/Window catalog (Feeney task 1c).
 *
 * These products are merged into GET /products results so they appear in
 * Add Doors & Windows without waiting for admin uploads. Once the same
 * models are published on the backend (matching `id`), the local copies
 * are skipped to avoid duplicates.
 *
 * Models: assets/models/doors-windows/*.obj|.mtl
 * Previews: assets/images/doors_windows/generic/*.png
 */
export interface IDoorWindowProduct {
  id: string;
  application: string;
  description: string;
  left_menu_alias: string;
  productType: 'Door' | 'Window';
  productGroup: string;
  height: number;
  length: number;
  width: number;
  /** Sill offset above deck floor (cm). Doors use 0. */
  defaultHeightFromFloor: number;
  modelObj: string;
  modelMtl: string;
  /** Absolute app path for menu tile (local asset). */
  localProductImg: string;
  productImg: string;
  isGeneric: boolean;
  /** Load OBJ/MTL from /assets instead of the API model store. */
  localAsset: boolean;
}

const MODEL_DIR = 'doors-windows/';
const IMG_DIR = '/assets/images/doors_windows/generic/';

function door(
  id: string,
  key: string,
  alias: string,
  dims: { height: number; length: number; width?: number },
  defaultHeightFromFloor = 0
): IDoorWindowProduct {
  return {
    id,
    application: 'Home',
    description: alias,
    left_menu_alias: alias,
    productType: 'Door',
    productGroup: 'Generic',
    height: dims.height,
    length: dims.length,
    width: dims.width != null ? dims.width : 28,
    defaultHeightFromFloor,
    modelObj: `${MODEL_DIR}${key}.obj`,
    modelMtl: `${MODEL_DIR}${key}.mtl`,
    localProductImg: `${IMG_DIR}${key}.png`,
    productImg: `${key}.png`,
    isGeneric: true,
    localAsset: true
  };
}

function window(
  id: string,
  key: string,
  alias: string,
  dims: { height: number; length: number; width?: number },
  defaultHeightFromFloor = 65
): IDoorWindowProduct {
  return {
    id,
    application: 'Home',
    description: alias,
    left_menu_alias: alias,
    productType: 'Window',
    productGroup: 'Generic',
    height: dims.height,
    length: dims.length,
    width: dims.width != null ? dims.width : 25,
    defaultHeightFromFloor,
    modelObj: `${MODEL_DIR}${key}.obj`,
    modelMtl: `${MODEL_DIR}${key}.mtl`,
    localProductImg: `${IMG_DIR}${key}.png`,
    productImg: `${key}.png`,
    isGeneric: true,
    localAsset: true
  };
}

/** Extra generic doors/windows from Feeney “New Windows & Doors Options” art. */
export const GENERIC_DOORS_WINDOWS: IDoorWindowProduct[] = [
  door('local_dw_modern_slat_door', 'modern_slat_door', 'Modern Slat Door', {
    height: 217,
    length: 185
  }),
  door('local_dw_craftsman_door', 'craftsman_door', 'Craftsman Entry Door', {
    height: 217,
    length: 160
  }),
  door('local_dw_modern_panel_door', 'modern_panel_door', 'Modern Panel Door', {
    height: 217,
    length: 165
  }),
  door('local_dw_french_doors', 'french_doors', 'French Doors', {
    height: 240,
    length: 185
  }),
  door('local_dw_minimalist_black_door', 'minimalist_black_door', 'Minimalist Black Door', {
    height: 240,
    length: 110
  }),
  door('local_dw_classic_6panel_door', 'classic_6panel_door', 'Classic 6-Panel Door', {
    height: 217,
    length: 104
  }),
  window('local_dw_multipane_window', 'multipane_window', 'Multi-Pane Window', {
    height: 152.4,
    length: 183
  }),
  window('local_dw_narrow_casement', 'narrow_casement', 'Narrow Casement Window', {
    height: 152.4,
    length: 61
  }),
  window(
    'local_dw_bow_window',
    'bow_window',
    'Bow Window',
    { height: 152.4, length: 200, width: 35 },
    65
  ),
  window('local_dw_sliding_glass', 'sliding_glass', 'Sliding Glass Window', {
    height: 152.4,
    length: 220
  }),
  window(
    'local_dw_tall_narrow_window',
    'tall_narrow_window',
    'Tall Narrow Window',
    { height: 200, length: 55 },
    40
  ),
  window('local_dw_double_multipane', 'double_multipane', 'Double Multi-Pane Window', {
    height: 152.4,
    length: 244
  }),
  window('local_dw_double_hung', 'double_hung', 'Double-Hung Window', {
    height: 152.4,
    length: 122
  }),
  window(
    'local_dw_small_double_hung',
    'small_double_hung',
    'Small Double-Hung Window',
    { height: 122, length: 76 },
    80
  )
];

export function mergeGenericDoorsWindows(apiProducts: any[]): any[] {
  const existingIds = new Set((apiProducts || []).map(p => p && p.id));
  const extras = GENERIC_DOORS_WINDOWS.filter(p => !existingIds.has(p.id));
  return [...(apiProducts || []), ...extras];
}



// WEBPACK FOOTER //
// ./src/app/models/2d3d/doors-windows.config.ts